import { useEffect, useMemo, useRef, useState } from 'react';
import { func, object, oneOf, shape } from 'prop-types';
import _ from 'lodash';

import FilterRegistry, { createFilterComponent } from '../filter/FilterRegistry';
import Filter, { SUMMARY_LENGTHS } from '../filter/Filter';
import ErrorMessage from '../../error-message/ErrorMessage';
import { useFilterState, FilterStateContainer } from '../../../stores/FilterStateStore';
import useStyles from './ReportDialogStyles';
import { timeRangeFilterId } from '../../filters';
import { LAST_24_HRS, LAST_30_DAYS } from '../../filters/time-range-filter/TimeRangeFilterConstants';
import { INTERVALS } from './RecurrenceOptions';
import { useDataService } from '../../../services/ApiDataService';

export const filtersOptionsType = shape({
    encodedState: object,
});

export const defaultFiltersOptions = {};

const INTERVAL_TO_TIMERANGE_MAP = {
    [INTERVALS.DAILY]: LAST_24_HRS,
    [INTERVALS.WEEKLY]: LAST_24_HRS,
    [INTERVALS.MONTHLY]: LAST_30_DAYS,
};

function FilterContainer({ defaultEncodedFilterState, onChange }) {
    // This is the filter state from the FilterStateContainer and not the global filter state.
    // This ensures that the changes to the report filter state do not affect the global filter
    // state.
    // See the react-sweet-state documentation for more information about containers:
    // https://atlassian.github.io/react-sweet-state/#/advanced/container
    const [filterState, filterStateActions] = useFilterState();
    const [timeRangeChanged, setTimeRangeChanged] = useState(false);
    const classes = useStyles();

    useEffect(() => {
        // Since our filter state is from a container and not the global state, it is empty and needs to
        // be initialized.  The defaultEncodedFilterState prop contains either the saved report filter state
        // (when editing a saved report) or the current global filter state (when creating a new report)
        // which is used here to intitialize the container's state.
        filterStateActions.initialize(defaultEncodedFilterState);
        // This intentionally disables the exhaustive dependency check because this is initialization meant
        // to run ONLY when the component is mounted, so we don't want it to run when its dependencies change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (timeRangeChanged) return;
        const defaultState = defaultEncodedFilterState[timeRangeFilterId];

        if (!defaultState) return;

        const timeRangeFilterStateService = FilterRegistry.getFilterStateService(timeRangeFilterId);
        const timeRangeFilterState = timeRangeFilterStateService.decode(defaultState);
        filterStateActions.setFilterState(timeRangeFilterId, timeRangeFilterState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultEncodedFilterState]);

    useEffect(() => {
        if (!filterState) return;
        setTimeRangeChanged(defaultEncodedFilterState[timeRangeFilterId] !== filterState.encoded[timeRangeFilterId]);
        onChange(filterState.encoded);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState]);

    return (
        <div className={classes.filterContainer}>
            {!!filterState &&
                Object.keys(filterState.current).map((filterId) => {
                    try {
                        return (
                            <Filter key={filterId} classes={classes} summaryLength={SUMMARY_LENGTHS.SHORT}>
                                {createFilterComponent(filterId, {
                                    required: true,
                                    autoRefresh: false,
                                    options: filterId === timeRangeFilterId ? { enableCustom: false } : {},
                                })}
                            </Filter>
                        );
                    } catch (error) {
                        return (
                            <ErrorMessage
                                key={filterId}
                                id={filterId}
                                error={error}
                                componentStack={error.stack || ''}
                            />
                        );
                    }
                })}
        </div>
    );
}

FilterContainer.propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    defaultEncodedFilterState: object.isRequired,
    onChange: func.isRequired,
};

export default function FilterOptions({ recurrenceInterval, options, onChange, onValidate }) {
    const classes = useStyles();
    const onChangeCallback = useRef();
    const onValidateCallback = useRef();
    const [filterState] = useFilterState();
    const defaultEncodedFilterState = useMemo(() => {
        if (options?.encodedState) return options.encodedState;

        // No previously saved state, so create it from the current state.
        return _.mapValues(filterState.current, (state, filterId) => {
            const filterStateService = FilterRegistry.getFilterStateService(filterId);
            return filterStateService.encode(
                filterId === timeRangeFilterId
                    ? {
                          ...state,
                          // Override the time range to use the default for the recurrence interval.
                          id: INTERVAL_TO_TIMERANGE_MAP[recurrenceInterval].id,
                      }
                    : state
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recurrenceInterval]);
    const [encodedState, setEncodedState] = useState(defaultEncodedFilterState);

    onChangeCallback.current = onChange;
    onValidateCallback.current = onValidate;

    useEffect(() => {
        onValidateCallback.current(true);
        onChangeCallback.current({
            encodedState,
        });
    }, [encodedState]);

    return (
        <div className={classes.filterOptions}>
            <FilterStateContainer name="scheduled-report-filter-state" dataService={useDataService()} manageUrl={false}>
                <FilterContainer defaultEncodedFilterState={defaultEncodedFilterState} onChange={setEncodedState} />
            </FilterStateContainer>
        </div>
    );
}

FilterOptions.propTypes = {
    recurrenceInterval: oneOf(Object.values(INTERVALS)),
    options: filtersOptionsType,
    onChange: func.isRequired,
    onValidate: func.isRequired,
};

FilterOptions.defaultProps = {
    recurrenceInterval: INTERVALS.DAILY,
    options: undefined,
};
